import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon, { type IconName } from './Icon';
import StocoraLogo from './StocoraLogo';
import {
  useSession,
  setViewAsRole,
  type ClinicRole,
} from '@/lib/session';
import { realRoleHasPermission } from '@/lib/permissions';
import { logout } from '@/lib/auth';

/**
 * Header — wired to real session data in 1B.3, role display from 1B.5,
 * dropdown menu + sign-out flow added in 1B.6, viewAsRole picker
 * added in 3D.2.
 *
 * Two exports:
 *
 *   - default `Header` reads from the session store and owns the
 *     menu state machine. Use inside the authenticated branch of
 *     AuthenticatedLayout.
 *
 *   - `HeaderShell` is the bare chrome with a static (non-interactive)
 *     pill, used by the auth/clinic loading states where no session
 *     exists yet.
 *
 * Menu shape:
 *   - Tap the role pill to open the menu.
 *   - Menu shows account context (email + clinic), an optional
 *     "View as" picker (owner-only per 3D.2), and a Sign out item.
 *   - Tapping Sign out transitions the menu into a confirmation step
 *     (Cancel / Sign out) so accidental taps don't drop a clinic
 *     worker out of an inventory session.
 *   - Tapping a View-as radio applies the impersonation and closes
 *     the menu — no confirmation step, no side effects beyond
 *     swapping the PWA permission cache.
 *   - Click outside or Escape closes the menu in any state.
 *   - Tapping the pill while the menu is open closes it.
 *
 * Pill behavior under impersonation (3D.2):
 *   - The pill icon + label reflect the IMPERSONATED role, not the
 *     real role. Background tints amber to differentiate from the
 *     normal pill, and pairs visually with the ViewAsBanner above.
 *
 * Future tickets (multi-clinic switcher, settings) plug into the
 * same dropdown by adding rows.
 */

const ROLE_ICON: Record<ClinicRole, IconName> = {
  owner: 'shield',
  manager: 'shield',
  staff: 'user',
};

const ROLE_LABEL: Record<ClinicRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  staff: 'Staff',
};

// Display order for the View-as picker. Real-role-first so the
// "(you)" row is always at the top of the available choices.
const ROLE_PICKER_ORDER: readonly ClinicRole[] = [
  'owner',
  'manager',
  'staff',
] as const;

type MenuState = 'closed' | 'open' | 'confirming';

export default function Header() {
  const session = useSession();
  const { activeMembership, email, viewAsRole } = session;
  const [menuState, setMenuState] = useState<MenuState>('closed');
  const containerRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setMenuState('closed'), []);

  // Click-outside + Escape close the menu in any non-closed state.
  useEffect(() => {
    if (menuState === 'closed') return;

    const onClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        close();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuState, close]);

  // Focus the confirm button when entering the confirming state so
  // keyboard users land on the destructive action explicitly.
  useEffect(() => {
    if (menuState === 'confirming') {
      confirmButtonRef.current?.focus();
    }
  }, [menuState]);

  const onPillClick = (): void => {
    setMenuState((s) => (s === 'closed' ? 'open' : 'closed'));
  };
  const onSignOutTap = (): void => setMenuState('confirming');
  const onCancelTap = (): void => setMenuState('open');
  const onConfirmSignOutTap = (): void => {
    setMenuState('closed');
    void logout();
  };

  const realRole = activeMembership.role;
  // The role surfaced in the pill chrome. When impersonating,
  // show the impersonated role so the chrome agrees with what
  // the rest of the UI is rendering.
  const displayRole: ClinicRole = viewAsRole ?? realRole;
  const isOpen = menuState !== 'closed';
  const isImpersonating = viewAsRole !== null;

  // Picker visibility gates on the REAL role's catalog defaults —
  // not the effective permission cache. Without this, selecting
  // "view as Staff" would hide the toggle that gets you back out.
  const canImpersonate = realRoleHasPermission(session, 'system.view_as_role');

  // The currently-checked option in the picker: impersonated role
  // when active, else the user's real role.
  const pickerSelection: ClinicRole = viewAsRole ?? realRole;

  const onPickRole = (role: ClinicRole): void => {
    // setViewAsRole normalizes role === realRole to null (clears).
    setViewAsRole(role);
    setMenuState('closed');
  };

  return (
    <header className="bg-white border-b border-slate-200/80 px-4 pt-3 pb-3 relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <StocoraLogo size={20} />
          <div className="leading-tight min-w-0 border-l border-slate-200 pl-2.5">
            <div className="text-[11px] text-slate-500 truncate max-w-[200px]">
              {activeMembership.clinic_name}
            </div>
          </div>
        </div>

        <div ref={containerRef} className="relative">
          <button
            type="button"
            onClick={onPillClick}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-label={
              isImpersonating
                ? `Account menu — demo mode, viewing as ${ROLE_LABEL[displayRole]}, real role ${ROLE_LABEL[realRole]}`
                : `Account menu — signed in as ${ROLE_LABEL[displayRole]}`
            }
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition ${
              isImpersonating
                ? isOpen
                  ? 'bg-amber-200 text-amber-900'
                  : 'bg-amber-100 text-amber-900 hover:bg-amber-200 active:bg-amber-300'
                : isOpen
                  ? 'bg-slate-200 text-slate-900'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300'
            }`}
          >
            <Icon
              name={ROLE_ICON[displayRole]}
              className="w-3.5 h-3.5"
              strokeWidth={2}
            />
            {ROLE_LABEL[displayRole]}
            <Icon
              name="chevdown"
              className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              strokeWidth={2.5}
            />
          </button>

          {isOpen && (
            <div
              role="menu"
              className="absolute right-0 top-[40px] z-50 w-60 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
            >
              {menuState === 'open' ? (
                <>
                  <div className="px-3 py-2.5 border-b border-slate-100">
                    <div className="text-[12px] font-medium text-slate-900 truncate">
                      {email || 'Signed in'}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      {activeMembership.clinic_name}
                    </div>
                  </div>

                  {canImpersonate && (
                    <div className="border-b border-slate-100 py-2">
                      <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        View as
                      </div>
                      {ROLE_PICKER_ORDER.map((r) => {
                        const isSelected = pickerSelection === r;
                        const isReal = r === realRole;
                        return (
                          <button
                            key={r}
                            type="button"
                            role="menuitemradio"
                            aria-checked={isSelected}
                            onClick={() => onPickRole(r)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[13px] text-slate-800 hover:bg-slate-50 active:bg-slate-100 transition"
                          >
                            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              {isSelected && (
                                <Icon
                                  name="check"
                                  className="w-4 h-4 text-action"
                                  strokeWidth={2.5}
                                />
                              )}
                            </span>
                            <span className="flex-1">
                              {ROLE_LABEL[r]}
                              {isReal && (
                                <span className="text-slate-400 font-normal">
                                  {' '}
                                  (you)
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <Link
                    to="/settings"
                    role="menuitem"
                    onClick={close}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50 active:bg-slate-100 transition"
                  >
                    <Icon
                      name="shield"
                      className="w-4 h-4 text-slate-500"
                      strokeWidth={2}
                    />
                    Settings
                  </Link>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={onSignOutTap}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-slate-800 hover:bg-slate-50 active:bg-slate-100 transition"
                  >
                    <Icon
                      name="logout"
                      className="w-4 h-4 text-slate-500"
                      strokeWidth={2}
                    />
                    Sign out
                  </button>
                </>
              ) : (
                <div className="p-3" role="alertdialog" aria-label="Confirm sign out">
                  <p
                    className="text-[13px] text-slate-700 leading-snug"
                    aria-live="polite"
                  >
                    Sign out of{' '}
                    <span className="font-medium text-slate-900">
                      {activeMembership.clinic_name}
                    </span>
                    ?
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={onCancelTap}
                      className="flex-1 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 transition text-xs font-medium text-slate-700"
                    >
                      Cancel
                    </button>
                    <button
                      ref={confirmButtonRef}
                      type="button"
                      onClick={onConfirmSignOutTap}
                      className="flex-1 h-9 rounded-lg bg-red-600 hover:bg-red-700 active:bg-red-800 transition text-xs font-semibold text-white"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ---- HeaderShell (loading-state chrome, unchanged from 1B.5) -------

export interface HeaderShellProps {
  clinicName: string;
  roleIcon: IconName;
  roleLabel: string;
  /** When true, subtitle + role pill render as empty/muted — used
   *  by loading states before session data is available. */
  muted?: boolean;
}

export function HeaderShell({
  clinicName,
  roleIcon,
  roleLabel,
  muted = false,
}: HeaderShellProps) {
  return (
    <header className="bg-white border-b border-slate-200/80 px-4 pt-3 pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <StocoraLogo size={20} />
          <div className="leading-tight min-w-0 border-l border-slate-200 pl-2.5">
            <div
              className={`text-[11px] truncate max-w-[200px] ${
                muted ? 'text-slate-300' : 'text-slate-500'
              }`}
            >
              {muted ? '\u00A0' : clinicName}
            </div>
          </div>
        </div>

        <div
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ${
            muted
              ? 'bg-slate-50 text-slate-300'
              : 'bg-slate-100 text-slate-700'
          }`}
          aria-label={muted ? undefined : `Role: ${roleLabel}`}
        >
          {!muted && (
            <>
              <Icon name={roleIcon} className="w-3.5 h-3.5" strokeWidth={2} />
              {roleLabel}
            </>
          )}
        </div>
      </div>
    </header>
  );
}
